/* ==========================================================================
   BC Delivery progress bar (Rebuy removal, stage 4, part 2).

   READS, NEVER CALCULATES. Every figure comes from the data-bar-* values that
   snippets/bc-delivery-bar-state.liquid stamps on .cart__items (and, for first
   paint, on the bar itself). All amounts are integer pence. Full spec:
   rebuy-removal-plan.md section 7.

   THE ONE RULE: the bar only promises what checkout will deliver.
     - UK in GBP only (data-bar-eligible). Nothing anywhere else.
     - While an ORDER CODE is applied it shows only what the cart already
       proves (a trolley line at £0). No "unlocked", no "£X away", no
       "qualified": a code that doesn't tick Shipping/Product discounts
       competes with the offer instead of stacking, and the cart can't show
       which way that went.
     - No product ships free by itself. UK delivery is priced by basket total
       in Settings → Shipping and delivery, so every basket counts towards the
       free threshold the same way. (The old per-product "ships free" message,
       which mirrored the Intuitive Shipping app, was removed with the app.)

   Updates on every cart change: a childList observer on each bar's own
   [data-products], the element theme.js replaces on every rebuild — drawer and
   cart page alike. The drawer's bc:cart:updated is not used because it is
   drawer-only; the observer covers both surfaces with one mechanism.
   ========================================================================== */

(function () {
  'use strict';

  var SCOPE = '.bc-drawer, #CartPageForm';

  function num(el, name) {
    var v = parseInt(el.getAttribute('data-bar-' + name), 10);
    return isFinite(v) ? v : 0;
  }

  /* The freshest state: the .cart__items the last rebuild appended, else the
     state stamped on the bar at first paint. */
  function source(bar) {
    var scope = bar.closest(SCOPE);
    var items = scope && scope.querySelector('[data-products] .cart__items[data-bar-eligible]');
    return items || bar;
  }

  /* Whole pounds, ROUNDED UP, so £399.77 reads "£1 away", never "£0 away".
     Formatted here rather than through theme.Currency: the bar only ever shows
     in GBP, and this keeps it independent of theme.js load order (a deferred
     script can run before theme.js). Matches the store's £{{amount_no_decimals}}. */
  function poundsUp(pence) {
    var whole = Math.ceil(pence / 100);
    return '£' + String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function amountEl(text) {
    var strong = document.createElement('strong');
    strong.className = 'bc-bar__amount';
    strong.textContent = text;
    return strong;
  }

  /* The Liquid fills a locale string's {{ price }} with this token, so each
     language puts the trolley's price where its grammar wants it. */
  var PRICE_TOKEN = '[price]';

  /* THE INFO BUTTON NEVER SITS ON A LINE BY ITSELF. It goes into one no-wrap
     group (.bc-bar__keep) with whatever it follows, and the two wrap together:
     the trolley's price where the price ends the words — every language but
     Japanese — else the last word ("…trolley added"), or the last character
     where the words have no spaces (Japanese). Without the group a line that
     exactly fills its width sends the button down alone: an inline button is
     a place the line may break, with or without a space before it. */
  function attachInfo(span, info) {
    var keep = document.createElement('span');
    keep.className = 'bc-bar__keep';
    var last = span.lastChild;
    // A translation that ends in a space: drop the space, or the price before
    // it would be left outside the group.
    while (last && last.nodeType === 3 && !/\S/.test(last.nodeValue)) {
      span.removeChild(last);
      last = span.lastChild;
    }
    if (last && last.nodeType === 3) {
      var text = last.nodeValue.replace(/\s+$/, '');
      var cut = text.lastIndexOf(' ') + 1;
      if (!cut && text.length > 1) {
        cut = text.length - 1;
        if (/[\uDC00-\uDFFF]/.test(text.charAt(cut)) && cut > 0) cut -= 1;   // never half a character
      }
      keep.appendChild(document.createTextNode(text.slice(cut)));
      if (cut) last.nodeValue = text.slice(0, cut); else span.removeChild(last);
    } else if (last) {
      keep.appendChild(last);
    }
    keep.appendChild(info);
    span.appendChild(keep);
  }

  /* Amount and words in separate nodes, so the words are one fixed string for
     Langify to key; the trolley's price, where the words carry one, is its own
     node too. A met goal leads with the tick, cloned from the snippet's own so
     every tick is the same markup. The info button goes inside the words, kept
     with their end by attachInfo(). */
  function setLine(el, amount, words, done, tick, price, info) {
    el.textContent = '';
    if (done && tick) el.appendChild(tick.cloneNode(true));
    if (amount) {
      el.appendChild(amountEl(amount));
      el.appendChild(document.createTextNode(' '));
    }
    var span = document.createElement('span');
    words.split(PRICE_TOKEN).forEach(function (part, i) {
      if (i > 0) span.appendChild(amountEl(price));
      if (part) span.appendChild(document.createTextNode(part));
    });
    if (info) attachInfo(span, info);
    el.appendChild(span);
    el.classList.toggle('bc-bar__line--done', !!done);
    el.hidden = false;
  }

  /* The trolley panel's status line, beside its X. Found by id, not inside the
     bar: on the cart page the panel has been moved to <body>. Same split as
     the bar's message — the amount bold in its own node, the words after it.
     Hidden when there is no trolley tier to report (no tent, or a code that
     may stop the trolley being free). */
  function setPanelStatus(bar, trolley, t) {
    var tpl = bar.querySelector('template[data-bc-trolley-info-button]');
    var btn = tpl && tpl.content.firstElementChild;
    var panel = btn && document.getElementById(btn.getAttribute('aria-controls'));
    var el = panel && panel.querySelector('[data-bc-trolley-status]');
    if (!el) return;
    el.textContent = '';
    if (!trolley) {
      el.hidden = true;
      return;
    }
    if (trolley.kind === 'away') {
      el.appendChild(amountEl(trolley.amount));
      el.appendChild(document.createTextNode(' '));
    }
    var span = document.createElement('span');
    span.textContent = t('trolley-panel-' + trolley.kind);
    el.appendChild(span);
    el.hidden = false;
  }

  /* A fresh info button for the trolley message, or null where the snippet
     rendered none (no trolley product set). */
  function infoButton(bar) {
    var tpl = bar.querySelector('template[data-bc-trolley-info-button]');
    return tpl && tpl.content.firstElementChild ? tpl.content.firstElementChild.cloneNode(true) : null;
  }

  function render(bar) {
    var s = source(bar);
    var t = function (k) { return bar.getAttribute('data-t-' + k) || ''; };

    var count = num(s, 'count');
    var eligible = num(s, 'eligible') === 1;
    var code = num(s, 'order-code') === 1;

    // ---- shipping tier -> { text, amount, done, pct } or null
    var ship = null;
    if (eligible && num(s, 'ship-enabled') === 1 && !code) {
      var total = num(s, 'basket-total');
      var shipAt = num(s, 'ship-threshold');
      ship = total >= shipAt
        ? { text: t('ship-unlocked'), done: true }
        : { text: t('ship-away'), amount: poundsUp(shipAt - total), pct: total / shipAt };
    }

    // ---- trolley tier: only with a tent in the basket
    var trolley = null;
    var tentsOver = false;
    if (eligible && num(s, 'trolley-enabled') === 1 && num(s, 'has-tent') === 1) {
      var tents = num(s, 'tent-subtotal');
      var trolleyAt = num(s, 'trolley-threshold');
      tentsOver = tents >= trolleyAt;
      if (num(s, 'trolley-free') === 1) {
        trolley = { kind: 'added', text: t('trolley-added'), done: true };      // read from the cart, not predicted
      } else if (!code) {
        trolley = tentsOver
          ? { kind: 'qualified', text: t('trolley-qualified'), done: true }        // never "added" until EG has added it
          : { kind: 'away', text: t('trolley-away'), amount: poundsUp(trolleyAt - tents), pct: tents / trolleyAt };
      }
    }

    // The panel's own status line, from the same tier — kept current even
    // while the panel is open, because this runs on every cart change.
    setPanelStatus(bar, trolley, t);

    // ---- trolley upsell: hidden once the tents qualify (drawer FBT only).
    //      Not while a code is applied — the trolley may not end up free.
    var drawer = bar.closest('.bc-drawer');
    if (drawer) {
      if (eligible && tentsOver && !code && num(s, 'trolley-enabled') === 1) {
        drawer.setAttribute('data-bc-hide-trolley', '');
      } else {
        drawer.removeAttribute('data-bc-hide-trolley');
      }
    }

    // ---- layout: ONE bar, one goal at a time. The tiers count different
    //      things (basket total, tent subtotal), so they never share a scale.
    //        delivery not met          -> delivery stage
    //        delivery met + trolley    -> ticked delivery line, trolley stage
    //        delivery met, no trolley  -> delivery stage, full
    //      No delivery tier at all (tier off, or a code) -> trolley stage
    //      alone.
    var done = bar.querySelector('[data-bc-bar-done]');
    var stageEl = bar.querySelector('[data-bc-bar-stage]');

    var stage = null;
    var doneText = null;
    if (ship && !ship.done) {
      stage = { key: 'ship', tier: ship };
    } else if (trolley) {
      stage = { key: 'trolley', tier: trolley };
      if (ship) doneText = ship.text;
    } else if (ship) {
      stage = { key: 'ship', tier: ship };
    }

    if (count === 0 || (!stage && !doneText)) {
      bar.hidden = true;
      return;
    }

    if (doneText) {
      bar.querySelector('[data-bc-bar-done-text]').textContent = doneText;
      done.hidden = false;
    } else {
      done.hidden = true;
    }

    if (stage) {
      var tier = stage.tier;
      var trolleyStage = stage.key === 'trolley';
      setLine(bar.querySelector('[data-bc-bar-line]'), tier.amount, tier.text, tier.done, bar.querySelector('.bc-bar__tick'),
        bar.getAttribute('data-trolley-price') || '', trolleyStage ? infoButton(bar) : null);
      var pct = tier.done ? 1 : Math.max(0, Math.min(1, tier.pct));
      bar.querySelector('[data-bc-bar-fill]').style.width = pct * 100 + '%';
      Array.prototype.forEach.call(bar.querySelectorAll('[data-bc-bar-icon]'), function (icon) {
        icon.hidden = icon.getAttribute('data-bc-bar-icon') !== stage.key;
      });
      bar.querySelector('[data-bc-bar-target]').textContent = bar.getAttribute('data-target-' + stage.key) || '';
      stageEl.classList.toggle('is-met', !!tier.done);
      stageEl.hidden = false;
    } else {
      stageEl.hidden = true;
    }
    bar.hidden = false;
  }

  /* ==========================================================================
     THE TROLLEY PANEL — opened by the info button on the trolley message.

     Slides in from the right over the whole drawer (or, on the cart page,
     over the page's right side at the drawer's width) and back out on close.
     Modal within its surface: everything else in .bc-drawer — or, on the
     cart page, the rest of the page — is inert while it is open, so neither
     the keyboard nor a screen reader can reach what is behind it.
     bc-cart-drawer.js's own Tab wrap and sentinel skip inert content.

     ⚠ ESCAPE CLOSES THE PANEL ONLY. theme.js closes the drawer on a KEYUP of
     Escape on window, so closing on keydown is not enough — the keyup that
     follows would still reach it. Both are stopped in the capture phase on
     document, which runs before any window bubble listener.

     Focus goes to the close button on open, and back to the info button on
     close. A cart change re-renders the message while the panel is open, so
     the button is found again at close, by the aria-controls it carries.

     On the cart page the panel is moved to <body> the first time it opens:
     inside the cart form it would share whatever stacking context the page
     builds around it, and the sticky header could paint over it.

     Reduced motion: the CSS drops the transition, and this hides the panel
     at once on close instead of waiting for the slide.
     ========================================================================== */

  var SLIDE_MS = 320;   // the CSS transition is 0.28s; hidden after it ends

  var pop = { el: null, inerted: [], swallowUp: false, timer: null, page: false };

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function focusQuietly(el) {
    if (!el || typeof el.focus !== 'function') return;
    try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
  }

  /* Every sibling of every ancestor, up to the surface's root. Only what this
     sets is undone, so the drawer's own inert marks are never touched. */
  function inertAround(el, root) {
    pop.inerted = [];
    for (var node = el; node && node !== root && node.parentElement; node = node.parentElement) {
      Array.prototype.forEach.call(node.parentElement.children, function (sib) {
        if (sib === node || sib.hasAttribute('inert')) return;
        if (/^(SCRIPT|STYLE|LINK|META|TEMPLATE|NOSCRIPT)$/.test(sib.tagName)) return;
        sib.setAttribute('inert', '');
        pop.inerted.push(sib);
      });
    }
  }

  /* ---- the image carousel: native horizontal scroll, so swipe is free ---- */

  function updateArrows(el) {
    var track = el.querySelector('[data-bc-trolley-track]');
    var prev = el.querySelector('[data-bc-trolley-prev]');
    var next = el.querySelector('[data-bc-trolley-next]');
    if (!track || !prev || !next) return;
    var atStart = track.scrollLeft <= 1;
    var atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
    prev.classList.toggle('is-disabled', atStart);
    next.classList.toggle('is-disabled', atEnd);
    prev.setAttribute('aria-disabled', atStart ? 'true' : 'false');
    next.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
  }

  function bindGallery(el) {
    var track = el.querySelector('[data-bc-trolley-track]');
    if (!track) return;
    function step(dir) {
      return function (evt) {
        if (evt.currentTarget.classList.contains('is-disabled')) return;
        var instant = reducedMotion();
        track.scrollBy({ left: dir * track.clientWidth, behavior: instant ? 'auto' : 'smooth' });
        // Scroll events are tied to rendering frames; settle the arrows
        // after the scroll whether or not one arrives.
        setTimeout(function () { updateArrows(el); }, instant ? 50 : 450);
      };
    }
    var prev = el.querySelector('[data-bc-trolley-prev]');
    var next = el.querySelector('[data-bc-trolley-next]');
    if (prev) prev.addEventListener('click', step(-1));
    if (next) next.addEventListener('click', step(1));
    // Directly on scroll, not through requestAnimationFrame: rAF does not run
    // in a background tab, and the work is two reads and two class toggles.
    track.addEventListener('scroll', function () { updateArrows(el); }, { passive: true });
  }

  function infoButtonFor(el) {
    return document.querySelector('.bc-bar__info[aria-controls="' + el.id + '"]');
  }

  function openPanel(button) {
    var el = document.getElementById(button.getAttribute('aria-controls'));
    if (!el || pop.el) return;
    var drawer = el.closest('.bc-drawer');
    if (!drawer && el.parentNode !== document.body) document.body.appendChild(el);
    if (!el.hasAttribute('data-bc-bound')) {
      el.setAttribute('data-bc-bound', '');
      bindGallery(el);
    }
    clearTimeout(pop.timer);
    pop.el = el;
    pop.page = !drawer;
    el.hidden = false;
    var track = el.querySelector('[data-bc-trolley-track]');
    if (track) track.scrollLeft = 0;
    updateArrows(el);
    void el.offsetWidth;            // commit the off-screen position, so it slides
    el.classList.add('is-open');
    inertAround(el, drawer || document.body);
    if (pop.page) document.documentElement.classList.add('bc-trolley-panel-open');
    focusQuietly(el.querySelector('[data-bc-trolley-info-close]'));
  }

  /* refocus: false when the drawer itself is closing — its own close puts
     focus back where the drawer was opened from, and the panel goes at once
     rather than sliding inside a drawer that is sliding away. */
  function closePanel(refocus) {
    var el = pop.el;
    if (!el) return;
    el.classList.remove('is-open');
    pop.inerted.forEach(function (n) { n.removeAttribute('inert'); });
    pop.inerted = [];
    document.documentElement.classList.remove('bc-trolley-panel-open');
    pop.el = null;
    var hide = function () { if (!el.classList.contains('is-open')) el.hidden = true; };
    if (!refocus || reducedMotion()) hide(); else pop.timer = setTimeout(hide, SLIDE_MS);
    if (refocus) focusQuietly(infoButtonFor(el));
  }

  function startPanel() {
    document.addEventListener('click', function (evt) {
      var t = evt.target;
      if (!t.closest) return;
      var button = t.closest('.bc-bar__info');
      if (button) {
        openPanel(button);
        return;
      }
      // The close button, or (cart page) the backdrop beside the sheet.
      if (pop.el && (t.closest('[data-bc-trolley-info-close]') || t === pop.el)) closePanel(true);
    });

    document.addEventListener('keydown', function (evt) {
      if (!pop.el) return;
      if (evt.key === 'Escape' || evt.key === 'Esc') {
        evt.preventDefault();
        evt.stopPropagation();
        pop.swallowUp = true;
        closePanel(true);
      } else if (evt.key === 'Tab') {
        // Tab goes round the panel's own controls. The cart page has no trap
        // of its own, and in the drawer this runs after bc-cart-drawer.js's.
        var list = Array.prototype.filter.call(pop.el.querySelectorAll('a[href], button:not([disabled])'), function (el) {
          return el.getClientRects().length > 0;
        });
        if (!list.length) return;
        var i = list.indexOf(document.activeElement);
        var next = evt.shiftKey ? (i <= 0 ? list.length - 1 : i - 1) : (i === -1 || i === list.length - 1 ? 0 : i + 1);
        evt.preventDefault();
        focusQuietly(list[next]);
      }
    }, true);

    document.addEventListener('keyup', function (evt) {
      if ((evt.key === 'Escape' || evt.key === 'Esc') && (pop.swallowUp || pop.el)) {
        evt.stopPropagation();
        pop.swallowUp = false;
      }
    }, true);

    // The drawer closing (click outside it, its own close) takes the panel
    // with it, or the drawer would reopen with its contents still inert.
    var drawerPanel = document.getElementById('HeaderCart');
    if (drawerPanel && window.MutationObserver) {
      new MutationObserver(function () {
        if (pop.el && drawerPanel.contains(pop.el) && !drawerPanel.classList.contains('is-active')) closePanel(false);
      }).observe(drawerPanel, { attributes: true, attributeFilter: ['class'] });
    }
  }

  function init() {
    var bars = document.querySelectorAll('[data-bc-bar]');
    Array.prototype.forEach.call(bars, function (bar) {
      render(bar);
      var scope = bar.closest(SCOPE);
      var products = scope && scope.querySelector('[data-products]');
      if (products && window.MutationObserver) {
        new MutationObserver(function () { render(bar); }).observe(products, { childList: true });
      }
    });
    if (document.querySelector('[data-bc-trolley-info]')) startPanel();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
